import { useEffect, useMemo, useRef } from "react";
import { PageMeta } from "../app/PageMeta";
import { AboutSection } from "../components/sections/AboutSection";
import { ContactSection } from "../components/sections/ContactSection";
import { ProjectsSection } from "../components/sections/ProjectsSection";
import { StudioSection } from "../components/sections/StudioSection";
import { TeamSection } from "../components/sections/TeamSection";
import type { HomeSectionId } from "../content/siteContent";
import { useHomeSectionObserver } from "../hooks/useHomeSectionObserver";
import { cn } from "../lib/cn";
import { useAppStore } from "../store/useAppStore";

export function HomePage() {
  const studioRef = useRef<HTMLDivElement | null>(null);
  const projectsRef = useRef<HTMLDivElement | null>(null);
  const teamRef = useRef<HTMLDivElement | null>(null);
  const aboutRef = useRef<HTMLDivElement | null>(null);
  const contactRef = useRef<HTMLDivElement | null>(null);
  const setExperienceMode = useAppStore((state) => state.setExperienceMode);
  const setActiveGameSlug = useAppStore((state) => state.setActiveGameSlug);
  const currentSection = useAppStore((state) => state.currentSection);
  const targetSection = useAppStore((state) => state.targetSection);
  const isTransitioning = useAppStore((state) => state.isTransitioning);
  const transitionProgress = useAppStore((state) => state.transitionProgress);

  const sections = useMemo(
    () => [
      { id: "studio" as const, ref: studioRef },
      { id: "projects" as const, ref: projectsRef },
      { id: "team" as const, ref: teamRef },
      { id: "about" as const, ref: aboutRef },
      { id: "contact" as const, ref: contactRef }
    ],
    []
  );

  useHomeSectionObserver(sections);

  useEffect(() => {
    setExperienceMode("home");
    setActiveGameSlug(null);
  }, [setActiveGameSlug, setExperienceMode]);

  const getSectionPhase = (id: HomeSectionId) => {
    if (!isTransitioning || currentSection === targetSection) {
      return currentSection === id ? "active" : "hidden";
    }

    if (currentSection === id) {
      return transitionProgress < 0.74 ? "active" : "hidden";
    }

    if (targetSection === id) {
      return transitionProgress < 0.82 ? "hidden" : "active";
    }

    return "hidden";
  };

  return (
    <>
      <PageMeta
        description="7 Stones is a premium studio-style single-page experience built with React, React Three Fiber, and semantic DOM content."
        path="/"
        title="7 Stones | Studio"
      />

      <div className="experience-stack" data-transitioning={isTransitioning ? "true" : undefined}>
        <div
          className={cn("scene-section-shell", "scene-section-shell--hero")}
          data-phase={getSectionPhase("studio")}
          id="studio"
          ref={studioRef}
        >
          {/* #region Studio Section */}
          <StudioSection />
          {/* #endregion */}
        </div>

        <div
          className={cn("scene-section-shell", "scene-section-shell--projects")}
          data-phase={getSectionPhase("projects")}
          id="projects"
          ref={projectsRef}
        >
          {/* #region Projects Section */}
          <ProjectsSection />
          {/* #endregion */}
        </div>

        <div className={cn("scene-section-shell", "scene-section-shell--team")} data-phase={getSectionPhase("team")} id="team" ref={teamRef}>
          {/* #region Team Section */}
          <TeamSection />
          {/* #endregion */}
        </div>

        <div
          className={cn("scene-section-shell", "scene-section-shell--about")}
          data-phase={getSectionPhase("about")}
          id="about"
          ref={aboutRef}
        >
          {/* #region About Section */}
          <AboutSection />
          {/* #endregion */}
        </div>

        <div
          className={cn("scene-section-shell", "scene-section-shell--contact")}
          data-phase={getSectionPhase("contact")}
          id="contact"
          ref={contactRef}
        >
          {/* #region Contact Section */}
          <ContactSection />
          {/* #endregion */}
        </div>
      </div>
    </>
  );
}
